import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PageHero from '../components/common/PageHero';
import PrimaryButton from '../components/common/PrimaryButton';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { MapPin, Phone, Building2, Navigation, Search, Mail, ExternalLink, Map as MapIcon, List as ListIcon, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API = process.env.REACT_APP_BACKEND_URL;
const SA_CENTER = [-30.5595, 22.9375];

const Practitioners = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [practitioners, setPractitioners] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('list');
  const [mapPoints, setMapPoints] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const { toast } = useToast();
  const mapRef = useRef(null);

  const provinces = ['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/practitioners`);
        if (!cancelled) {
          setPractitioners(data.members || []);
          setFiltered(data.members || []);
        }
      } catch {
        if (!cancelled) toast({ title: 'Failed to load practitioners', variant: 'destructive' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    let result = [...practitioners];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((p) =>
        (p.full_name || '').toLowerCase().includes(q) ||
        (p.city || '').toLowerCase().includes(q) ||
        (p.province || '').toLowerCase().includes(q) ||
        (p.practice_name || '').toLowerCase().includes(q)
      );
    }
    if (selectedProvince) {
      result = result.filter((p) => (p.province || '') === selectedProvince);
    }
    if (sortBy === 'name') {
      result.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    } else if (sortBy === 'distance-asc') {
      result.sort((a, b) => {
        if (a.distance_km == null) return 1;
        if (b.distance_km == null) return -1;
        return a.distance_km - b.distance_km;
      });
    } else if (sortBy === 'distance-desc') {
      result.sort((a, b) => {
        if (a.distance_km == null) return 1;
        if (b.distance_km == null) return -1;
        return b.distance_km - a.distance_km;
      });
    }
    setFiltered(result);
    setCurrentPage(1);
  }, [searchQuery, selectedProvince, practitioners, sortBy]);

  const getInitials = (name) => {
    const parts = (name || '').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name || '??').substring(0, 2).toUpperCase();
  };

  const getInitialsColor = (initials) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-yellow-500', 'bg-teal-500'];
    return colors[initials.charCodeAt(0) % colors.length];
  };

  const handleNearMe = () => {
    if (!('geolocation' in navigator)) {
      toast({ title: 'Geolocation not supported', variant: 'destructive' });
      return;
    }
    setNearMeLoading(true);
    toast({ title: 'Getting your location…', description: 'Please allow location access.' });
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        try {
          const { data } = await axios.get(`${API}/api/practitioners`, {
            params: { lat: latitude, lng: longitude, limit: 200 },
            timeout: 30000,
          });
          setPractitioners(data.members || []);
          setFiltered(data.members || []);
          setSortBy('distance-asc');
          const withDist = (data.members || []).filter(m => m.distance_km != null).length;
          toast({
            title: 'Sorted by distance',
            description: withDist > 0
              ? `${withDist} practitioners found near you. More distances calculating in background.`
              : 'Practitioners loaded. Distances will appear as addresses are geocoded.',
          });
        } catch {
          toast({ title: 'Failed to sort by distance', variant: 'destructive' });
        } finally {
          setNearMeLoading(false);
        }
      },
      () => {
        setNearMeLoading(false);
        toast({ title: 'Location access denied', variant: 'destructive' });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const showAll = () => {
    setUserLocation(null);
    setSortBy('name');
    setSearchQuery('');
    setSelectedProvince('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/practitioners`);
        setPractitioners(data.members || []);
        setFiltered(data.members || []);
      } catch {
        toast({ title: 'Failed to load practitioners', variant: 'destructive' });
      }
    })();
  };

  const openModal = (p) => { setSelectedPractitioner(p); setIsModalOpen(true); };

  const fetchMapData = async () => {
    setMapLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/practitioners/map-data`);
      setMapPoints(data.points || []);
    } catch {
      toast({ title: 'Failed to load map data', variant: 'destructive' });
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'map' && mapPoints.length === 0 && !mapLoading) {
      fetchMapData();
    }
  }, [viewMode]);

  const mapCenter = userLocation ? [userLocation.lat, userLocation.lng] : SA_CENTER;
  const mapZoom = userLocation ? 11 : 6;

  return (
    <div className="min-h-screen" data-testid="practitioners-page">
      <PageHero title="Find a Practitioner" subtitle="Locate qualified sports chiropractors near you" />

      <div className="container mx-auto px-4 py-12">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <PrimaryButton onClick={handleNearMe} variant="primary" size="md" className="whitespace-nowrap" data-testid="near-me-btn" disabled={nearMeLoading}>
                {nearMeLoading ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Navigation size={18} className="mr-2" />}
                Near Me
              </PrimaryButton>
              <PrimaryButton variant="outline" onClick={showAll} size="md" className="whitespace-nowrap" data-testid="show-all-btn">
                Show All
              </PrimaryButton>
              <div className="ml-auto flex gap-2">
                <PrimaryButton
                  variant={viewMode === 'list' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <ListIcon size={16} className="mr-1" /> List
                </PrimaryButton>
                <PrimaryButton
                  variant={viewMode === 'map' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                >
                  <MapIcon size={16} className="mr-1" /> Map
                </PrimaryButton>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Search by name, city, province or practice"
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="practitioner-search-input"
                />
              </div>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                data-testid="province-filter"
              >
                <option value="">All Provinces</option>
                {provinces.map((prov) => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-by"
              >
                <option value="name">Name (A-Z)</option>
                <option value="distance-asc">Distance (Nearest)</option>
                <option value="distance-desc">Distance (Farthest)</option>
              </select>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                data-testid="items-per-page"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>

            {viewMode === 'map' ? (
              <div>
                {mapLoading ? (
                  <div className="flex justify-center items-center h-[500px]">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="rounded-lg overflow-hidden shadow-lg" style={{ height: '600px' }}>
                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      style={{ height: '100%', width: '100%', zIndex: 1 }}
                      ref={mapRef}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {userLocation && (
                        <Marker position={[userLocation.lat, userLocation.lng]}>
                          <Popup>
                            <div>
                              <p className="font-semibold text-sm">Your Location</p>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      {mapPoints.map((pt) => (
                        <Marker key={pt.id} position={[pt.lat, pt.lng]}>
                          <Popup>
                            <div className="min-w-[200px]">
                              <p className="font-semibold text-sm">{pt.full_name}</p>
                              {pt.practice_name && <p className="text-xs text-gray-600">{pt.practice_name}</p>}
                              {pt.address && <p className="text-xs text-gray-500 mt-1">{pt.address}</p>}
                              {pt.public_phone && <p className="text-xs text-blue-600 mt-1">{pt.public_phone}</p>}
                              <button
                                onClick={() => {
                                  const p = practitioners.find(m => m.id === pt.id);
                                  if (p) openModal(p);
                                }}
                                className="text-xs text-blue-600 hover:underline mt-2 font-medium"
                              >
                                View Details →
                              </button>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-2 text-center">
                  {mapPoints.length > 0
                    ? `Showing ${mapPoints.length} practitioners on the map. Click a pin for details.`
                    : 'No practitioners with mapped locations yet. Try "Near Me" to geocode addresses.'}
                </p>
              </div>
            ) : (
              <>
                {loading ? (
                  <p className="text-center text-gray-500 py-12">Loading…</p>
                ) : filtered.length === 0 ? (
                  <p className="text-center text-gray-500 py-12">No practitioners found.</p>
                ) : (
                  <div className="space-y-4">
                    {paginated.map((p) => {
                      const initials = getInitials(p.full_name);
                      return (
                        <Card key={p.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openModal(p)} data-testid={`practitioner-card-${p.id}`}>
                          <CardContent className="p-6">
                            <div className="flex items-start space-x-4">
                              <div className={`${getInitialsColor(initials)} w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0`}>
                                {initials}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                                    {p.full_name}
                                  </h3>
                                  {p.distance_km != null && (
                                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full whitespace-nowrap">
                                      <Navigation size={14} />
                                      {p.distance_km} km
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center text-gray-600 space-x-2">
                                  <MapPin size={16} className="text-blue-600" />
                                  <span>{[p.city, p.province, p.country].filter(Boolean).join(', ')}</span>
                                </div>
                                {p.practice_name && (
                                  <div className="flex items-center text-gray-600 space-x-2 mt-1">
                                    <Building2 size={16} className="text-gray-400" />
                                    <span>{p.practice_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center items-center space-x-2 flex-wrap">
                    <PrimaryButton variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</PrimaryButton>
                    {[...Array(totalPages)].map((_, i) => (
                      <PrimaryButton key={i} variant={currentPage === i + 1 ? 'primary' : 'outline'} size="sm" onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </PrimaryButton>
                    ))}
                    <PrimaryButton variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</PrimaryButton>
                  </div>
                )}
              </>
            )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedPractitioner?.full_name}</DialogTitle>
          </DialogHeader>
          {selectedPractitioner && (
            <div className="space-y-4 py-4">
              {selectedPractitioner.distance_km != null && (
                <div className="flex items-start space-x-3">
                  <Navigation size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Distance from you</p>
                    <p className="text-gray-700">{selectedPractitioner.distance_km} km</p>
                  </div>
                </div>
              )}
              {selectedPractitioner.practice_name && (
                <div className="flex items-start space-x-3">
                  <Building2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Practice</p>
                    <p className="text-gray-700">{selectedPractitioner.practice_name}</p>
                  </div>
                </div>
              )}
              {selectedPractitioner.public_phone && (
                <div className="flex items-start space-x-3">
                  <Phone size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Contact</p>
                    <a href={`tel:${selectedPractitioner.public_phone}`} className="text-blue-600 hover:underline">{selectedPractitioner.public_phone}</a>
                  </div>
                </div>
              )}
              {selectedPractitioner.email && (
                <div className="flex items-start space-x-3">
                  <Mail size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <a href={`mailto:${selectedPractitioner.email}`} className="text-blue-600 hover:underline break-all">{selectedPractitioner.email}</a>
                  </div>
                </div>
              )}
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Location</p>
                  <p className="text-gray-700">
                    {selectedPractitioner.public_address_line1 && (<>{selectedPractitioner.public_address_line1}<br /></>)}
                    {selectedPractitioner.public_address_line2 && (<>{selectedPractitioner.public_address_line2}<br /></>)}
                    {[selectedPractitioner.city, selectedPractitioner.province].filter(Boolean).join(', ')}<br />
                    {selectedPractitioner.country}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([selectedPractitioner.public_address_line1, selectedPractitioner.public_address_line2, selectedPractitioner.city, selectedPractitioner.province, selectedPractitioner.country].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:underline text-sm mt-1"
                  >
                    Get Directions <ExternalLink size={14} className="ml-1" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Practitioners;
